import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import {
  ECellValueType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import { useEffect, useMemo } from 'react';
import ExcelTable from 'components/ui/excel-table';
import { TotalDeploymentPerBinderProps } from './types';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { contractApi } from 'services/apis';
import { isEmpty } from 'lodash';

const { Title } = Typography;

const TotalDeploymentPerBinderTable: React.FC<
  TotalDeploymentPerBinderProps
> = ({ submissionId, onErrorChange }) => {
  const { t } = useTranslation();

  const {
    data: totalDeploymentPerBinder,
    isLoading: isLoadingTotalDeploymentPerBinder,
  } = useQuery(
    [EQueryKeys.TOTAL_DEPLOYMENT_PER_BINDER, submissionId],
    () =>
      contractApi.getTotalValuePerBinder({
        submissionId,
      }),
    {
      enabled: !!submissionId,
    }
  );

  useEffect(() => {
    const hasError = totalDeploymentPerBinder?.some((item) => {
      return (
        item.excessOfLossAndQuotaShare.shareOfPremiumQuoted.isExceeded ||
        item.excessOfLossAndQuotaShare.shareOfLimitQuoted.isExceeded ||
        item.primary.shareOfLimitQuoted.isExceeded ||
        item.primary.shareOfPremiumQuoted.isExceeded
      );
    });
    onErrorChange?.(!!hasError);
  }, [totalDeploymentPerBinder]);

  // Transform API data to table format
  const tableData = useMemo(() => {
    if (!totalDeploymentPerBinder || isEmpty(totalDeploymentPerBinder))
      return [];

    return totalDeploymentPerBinder.map((item, index) => ({
      id: index + 1,
      binder: item.binderName,
      primaryShareOfLimit:
        parseFloat(item.primary.shareOfLimitQuoted.value) || 0,
      primaryShareOfPremium:
        parseFloat(item.primary.shareOfPremiumQuoted.value) || 0,
      primaryShareOfLimitExceeded: item.primary.shareOfLimitQuoted.isExceeded,
      primaryShareOfPremiumExceeded:
        item.primary.shareOfPremiumQuoted.isExceeded,
      excessOfLossAndQuotaShareShareOfLimit:
        parseFloat(item.excessOfLossAndQuotaShare.shareOfLimitQuoted.value) ||
        0,
      excessOfLossAndQuotaShareShareOfPremium:
        parseFloat(item.excessOfLossAndQuotaShare.shareOfPremiumQuoted.value) ||
        0,
      excessOfLossAndQuotaShareShareOfLimitExceeded:
        item.excessOfLossAndQuotaShare.shareOfLimitQuoted.isExceeded,
      excessOfLossAndQuotaShareShareOfPremiumExceeded:
        item.excessOfLossAndQuotaShare.shareOfPremiumQuoted.isExceeded,
    }));
  }, [totalDeploymentPerBinder]);

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        headerName: '',
        children: [
          {
            field: 'binder',
            headerName: t('binderDeployment.binder'),
            width: 240,
            editable: false,
            resizable: true,
          },
        ],
      },
      {
        headerName: t('binderDeployment.primary'),
        children: [
          {
            field: 'primaryShareOfLimit',
            headerName: t('binderDeployment.shareOfLimitQuoted'),
            width: 180,
            flex: 1,
            type: ECellValueType.NUMERIC_COLUMN,
            valueFormatter: (params) => {
              return params.value?.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            },
            editable: false,
            cellClassRules: {
              'bg-[#FEE2E2]': (params) => {
                return params.data?.primaryShareOfLimitExceeded === true;
              },
            },
            tooltipValueGetter: (params) => {
              if (params.data?.excessOfLossAndQuotaShareShareOfLimitExceeded) {
                return t('binderDeployment.binderShareExceedsLimit', {
                  limitType: 'Excess of Loss and Quota Share',
                });
              }

              if (params.data?.primaryShareOfLimitExceeded) {
                return t('binderDeployment.binderShareExceedsLimit', {
                  limitType: 'Primary',
                });
              }
              return '';
            },
          },
          {
            field: 'primaryShareOfPremium',
            headerName: t('binderDeployment.shareOfPremiumQuoted'),
            width: 180,
            flex: 1,
            type: ECellValueType.NUMERIC_COLUMN,
            valueFormatter: (params) => {
              return params.value?.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            },
            editable: false,
            cellClassRules: {
              'bg-[#FEE2E2]': (params) => {
                return params.data?.primaryShareOfPremiumExceeded === true;
              },
            },
            tooltipValueGetter: (params) => {
              if (params.data?.primaryShareOfPremiumExceeded) {
                return t('binderDeployment.binderPremiumExceedsLimit', {
                  limitType: 'Primary',
                });
              }
              return '';
            },
          },
        ],
      },
      {
        headerName: t('binderDeployment.excessOfLossAndQuotaShare'),
        children: [
          {
            field: 'excessOfLossAndQuotaShareShareOfLimit',
            headerName: t('binderDeployment.shareOfLimitQuoted'),
            width: 180,
            flex: 1,
            type: ECellValueType.NUMERIC_COLUMN,
            valueFormatter: (params) => {
              return params.value?.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            },
            editable: false,
            cellClassRules: {
              'bg-[#FEE2E2]': (params) => {
                return (
                  params.data?.excessOfLossAndQuotaShareShareOfLimitExceeded ===
                  true
                );
              },
            },
            tooltipValueGetter: (params) => {
              if (params.data?.excessOfLossAndQuotaShareShareOfLimitExceeded) {
                return t('binderDeployment.binderShareExceedsLimit', {
                  limitType: 'Excess of Loss and Quota Share',
                });
              }
              return '';
            },
          },
          {
            field: 'excessOfLossAndQuotaShareShareOfPremium',
            headerName: t('binderDeployment.shareOfPremiumQuoted'),
            width: 180,
            flex: 1,
            type: ECellValueType.NUMERIC_COLUMN,
            editable: false,
            valueFormatter: (params) => {
              return params.value?.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            },
            cellClassRules: {
              'bg-[#FEE2E2]': (params) => {
                return (
                  params.data
                    ?.excessOfLossAndQuotaShareShareOfPremiumExceeded === true
                );
              },
            },
            tooltipValueGetter: (params) => {
              if (
                params.data?.excessOfLossAndQuotaShareShareOfPremiumExceeded
              ) {
                return t('binderDeployment.binderPremiumExceedsLimit', {
                  limitType: 'Excess of Loss and Quota Share',
                });
              }
              return '';
            },
          },
        ],
      },
    ],
    [t]
  );

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <Title level={4} className="mb-6 ml-4">
        {t('binderDeployment.totalDeploymentPerBinderTable')}
      </Title>

      <ExcelTable
        data={tableData}
        columnDefs={columnDefs}
        pagination={false}
        includeIdRow={true}
        rowKeyColName="id"
        baseTableHeight={490}
        tableHeightBias={250}
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeAddRow={false}
        includeDeleteCol={false}
        loading={isLoadingTotalDeploymentPerBinder}
      />
    </div>
  );
};

export default TotalDeploymentPerBinderTable;
